// Generate a shortlist PDF report for a job opening.
// One cover page + one page per candidate, ranked by score.
// Dynamic import of jsPDF/autoTable to keep the main bundle lean.

export async function exportShortlistPdf({ opening, candidates, branch, topN = 5 }) {
  const { default: jsPDF } = await import('jspdf')
  const { default: autoTable } = await import('jspdf-autotable')

  const doc = new jsPDF({ unit: 'mm', format: 'a4' })
  const pw = doc.internal.pageSize.getWidth()
  const ph = doc.internal.pageSize.getHeight()
  const today = new Date().toISOString().slice(0, 10)

  const picked = candidates
    .slice()
    .sort((a, b) => (b.rankingScore || 0) - (a.rankingScore || 0))
    .slice(0, topN)

  // ── Cover page ──
  doc.setFont('helvetica', 'bold')
  doc.setFontSize(18)
  doc.text('DESRES KITCHEN & CONFECTIONERY', pw / 2, 30, { align: 'center' })
  doc.setFontSize(14)
  doc.text('Recruitment Shortlist Report', pw / 2, 40, { align: 'center' })

  doc.setFont('helvetica', 'normal')
  doc.setFontSize(11)
  const leftX = 25
  let y = 60
  doc.text(`Position: ${opening.jobTitle || '—'}`, leftX, y); y += 7
  doc.text(`Department: ${opening.department || '—'}`, leftX, y); y += 7
  doc.text(`Branch: ${branch || opening.branch || '—'}`, leftX, y); y += 7
  doc.text(`Date: ${today}`, leftX, y); y += 7
  doc.text(`Total candidates reviewed: ${candidates.length}`, leftX, y); y += 7
  doc.text(`Shortlisted: ${candidates.filter(c => c.shortlisted).length}`, leftX, y); y += 7
  doc.text(`Candidates in this report: ${picked.length}`, leftX, y); y += 14

  if (opening.requiredSkills?.length > 0) {
    doc.setFont('helvetica', 'bold')
    doc.setFontSize(10)
    doc.text('Required Skills:', leftX, y); y += 6
    doc.setFont('helvetica', 'normal')
    doc.setFontSize(9)
    const skillsText = doc.splitTextToSize(opening.requiredSkills.join(', '), pw - 50)
    doc.text(skillsText, leftX, y)
  }

  // Footer on cover
  doc.setFontSize(8)
  doc.setTextColor(140, 140, 140)
  doc.text('Generated by Desres OS — Confidential', pw / 2, ph - 10, { align: 'center' })
  doc.setTextColor(0)

  // ── Per-candidate pages ──
  picked.forEach((c, idx) => {
    doc.addPage()
    const rank = idx + 1
    let cy = 20

    doc.setFont('helvetica', 'bold')
    doc.setFontSize(14)
    doc.text(`#${rank} — ${c.fullName || '(unknown)'}`, leftX, cy); cy += 8

    doc.setFont('helvetica', 'normal')
    doc.setFontSize(11)
    const tier = c.rankingScore >= 90 ? 'Excellent'
      : c.rankingScore >= 70 ? 'Good'
      : c.rankingScore >= 50 ? 'Average'
      : 'Weak'
    doc.text(`Score: ${c.rankingScore || 0}/100 (${tier})`, leftX, cy); cy += 6
    if (c.recommendation) {
      doc.text(`Recommendation: ${c.recommendation}`, leftX, cy); cy += 6
    }
    cy += 2

    // Contact line
    doc.setFontSize(9)
    const contact = [c.phone, c.email, c.location, c.age != null ? `${c.age} yrs` : null]
      .filter(Boolean).join('  ·  ')
    if (contact) { doc.text(contact, leftX, cy); cy += 6 }
    cy += 2

    // Score breakdown table
    if (c.rankingBreakdown) {
      const b = c.rankingBreakdown
      autoTable(doc, {
        startY: cy,
        head: [['Category', 'Score', 'Max']],
        body: [
          ['Skills', b.skillsScore ?? 0, 30],
          ['Experience', b.experienceScore ?? 0, 25],
          ['Education', b.educationScore ?? 0, 20],
          ['Age Match', b.ageScore ?? 0, 10],
          ['Location', b.locationScore ?? 0, 10],
          ['Languages', b.languageScore ?? 0, 5],
          [{ content: 'TOTAL', styles: { fontStyle: 'bold' } }, { content: c.rankingScore || 0, styles: { fontStyle: 'bold' } }, '100'],
        ],
        styles: { fontSize: 8 },
        headStyles: { fillColor: [60, 60, 60] },
        margin: { left: leftX, right: 25 },
        theme: 'grid',
      })
      cy = doc.lastAutoTable.finalY + 6
    }

    // Assessment
    if (c.rankingReason) {
      doc.setFont('helvetica', 'italic')
      doc.setFontSize(9)
      const lines = doc.splitTextToSize(`"${c.rankingReason}"`, pw - 50)
      doc.text(lines, leftX, cy)
      cy += lines.length * 4 + 3
      doc.setFont('helvetica', 'normal')
    }

    // Strengths + weaknesses
    if (c.strengths?.length > 0) {
      if (cy > ph - 40) { doc.addPage(); cy = 20 }
      doc.setFont('helvetica', 'bold')
      doc.setFontSize(9)
      doc.text('Strengths:', leftX, cy); cy += 5
      doc.setFont('helvetica', 'normal')
      for (const s of c.strengths) {
        const lines = doc.splitTextToSize(`• ${s}`, pw - 55)
        doc.text(lines, leftX + 3, cy)
        cy += lines.length * 4
      }
      cy += 2
    }
    if (c.weaknesses?.length > 0) {
      if (cy > ph - 40) { doc.addPage(); cy = 20 }
      doc.setFont('helvetica', 'bold')
      doc.setFontSize(9)
      doc.text('Weaknesses:', leftX, cy); cy += 5
      doc.setFont('helvetica', 'normal')
      for (const w of c.weaknesses) {
        const lines = doc.splitTextToSize(`• ${w}`, pw - 55)
        doc.text(lines, leftX + 3, cy)
        cy += lines.length * 4
      }
      cy += 2
    }

    // Experience summary
    if (c.experience?.length > 0) {
      if (cy > ph - 40) { doc.addPage(); cy = 20 }
      doc.setFont('helvetica', 'bold')
      doc.setFontSize(9)
      doc.text('Experience:', leftX, cy); cy += 5
      doc.setFont('helvetica', 'normal')
      for (const e of c.experience) {
        if (cy > ph - 20) { doc.addPage(); cy = 20 }
        const parts = [e.role, e.company, e.duration].filter(Boolean).join(' — ')
        const lines = doc.splitTextToSize(`• ${parts}`, pw - 55)
        doc.text(lines, leftX + 3, cy)
        cy += lines.length * 4
      }
      cy += 2
    }

    // Education summary
    if (c.education?.length > 0) {
      if (cy > ph - 30) { doc.addPage(); cy = 20 }
      doc.setFont('helvetica', 'bold')
      doc.setFontSize(9)
      doc.text('Education:', leftX, cy); cy += 5
      doc.setFont('helvetica', 'normal')
      for (const e of c.education) {
        const parts = [e.qualification, e.field, e.institution, e.year].filter(Boolean).join(' — ')
        const lines = doc.splitTextToSize(`• ${parts}`, pw - 55)
        doc.text(lines, leftX + 3, cy)
        cy += lines.length * 4
      }
      cy += 2
    }

    // Skills summary
    if (c.skills?.length > 0) {
      if (cy > ph - 25) { doc.addPage(); cy = 20 }
      doc.setFont('helvetica', 'bold')
      doc.setFontSize(9)
      doc.text('Skills:', leftX, cy); cy += 5
      doc.setFont('helvetica', 'normal')
      const lines = doc.splitTextToSize(c.skills.join(', '), pw - 50)
      doc.text(lines, leftX, cy)
      cy += lines.length * 4
    }

    // Page footer
    doc.setFontSize(8)
    doc.setTextColor(140, 140, 140)
    doc.text('Generated by Desres OS — Confidential', pw / 2, ph - 10, { align: 'center' })
    doc.setTextColor(0)
  })

  const slug = (opening.jobTitle || 'shortlist').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '')
  doc.save(`shortlist_${slug}_${today}.pdf`)
}
